// src/Components/auto/ExamSimulator/StudentDashboard.jsx

import React, { useEffect, useState } from 'react';
import {
  FaSearch,
  FaCheckCircle,
  FaClock,
  FaBook,
  FaTrophy,
  FaChevronDown,
  FaChevronUp,
  FaRedo,
  FaChevronLeft,
  FaFilter,
  FaTags,
  FaInfoCircle,
} from 'react-icons/fa';
import ApiStore from '../../../api/ApiStore';
import { observer, inject } from 'mobx-react';
import DGTExamSimulator from './DGTExamSimulator';

const StudentDashboard = inject('store')(
  observer(({ store }) => {
    const organizationName = store.profile.organization || 'Madel';

    // Student's exam attempts
    const [studentExams, setStudentExams] = useState([]);
    // All exams in system
    const [allExams, setAllExams] = useState([]);
    const [loading, setLoading] = useState(false);

    // If user is actually taking an exam, show simulator
    const [selectedStudentExamId, setSelectedStudentExamId] = useState(null);
    const [examTitle, setExamTitle] = useState('');

    // Exam category management
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // For searching exams
    const [searchTerm, setSearchTerm] = useState('');

    // Track expanded categories and exam histories
    const [expandedExamIds, setExpandedExamIds] = useState([]);

    // Flag to indicate when categories are ready to be displayed
    const [categoriesReady, setCategoriesReady] = useState(false);

    // Example rules
    const examRules = [
      'Tienes 30 minutos para responder 30 preguntas',
      'Se permiten un máximo de 3 fallos para aprobar',
    ];

    // Fetch data on mount and when category changes
    useEffect(() => {
      let isMounted = true; // Add flag to prevent state updates if component unmounts

      async function fetchData() {
        setLoading(true);
        try {
          // Fetch all data in parallel
          const [categoriesData, studentRes, allExamsRes] = await Promise.all([
            ApiStore.getExamCategories(organizationName),
            ApiStore.getStudentExams(organizationName, selectedCategory),
            ApiStore.getAllExams(organizationName, selectedCategory),
          ]);

          // Only update state if component is still mounted
          if (isMounted) {
            // Set the categories and exam data
            setCategories(categoriesData || []);
            setStudentExams(studentRes?.data || []);
            setAllExams(allExamsRes || []);

            // Immediately expand all categories - no timeout needed
            if (categoriesData || allExamsRes) {
              // Get category IDs from the API response
              const categoryIds = categoriesData
                ? categoriesData.map(cat => `category_${cat}`)
                : [];

              // Add "Sin categoría" explicitly to ensure it's expanded by default
              categoryIds.push('category_Sin categoría');

              setExpandedExamIds(prevIds => {
                // Combine with any existing expanded IDs to avoid race conditions
                const uniqueIds = new Set([...prevIds, ...categoryIds]);
                return Array.from(uniqueIds);
              });

              // Set categories as ready
              setCategoriesReady(true);
            }
          }
        } catch (err) {
          console.error('Error fetching data for StudentDashboard:', err);
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      }

      fetchData();

      // Cleanup function
      return () => {
        isMounted = false;
      };
    }, [organizationName, selectedCategory]);

    // Handler to refresh data when coming back from exam - simplified
    const refreshDataFromExam = async () => {
      setLoading(true);
      try {
        // Just fetch student exams to update their status
        const studentRes = await ApiStore.getStudentExams(
          organizationName,
          selectedCategory
        );
        setStudentExams(studentRes?.data || []);
      } catch (err) {
        console.error('Error refreshing data after exam:', err);
      } finally {
        setLoading(false);
      }
    };

    // If user started an exam => show simulator
    if (selectedStudentExamId) {
      return (
        <DGTExamSimulator
          studentExamId={selectedStudentExamId}
          examTitle={examTitle}
          onClose={() => {
            setSelectedStudentExamId(null);
            refreshDataFromExam(); // Refresh data when returning from exam
          }}
        />
      );
    }

    // Filter all exams by searchTerm
    const filteredExams = allExams.filter(e =>
      e.ExamName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // For the exam status display: get number of attempts completed per exam
    const attemptCountMap = studentExams.reduce((acc, ex) => {
      if (ex.status === 'completed') {
        acc[ex.examId] = (acc[ex.examId] || 0) + 1;
      }
      return acc;
    }, {});

    // Map exam status data
    function getStatusForExam(exam) {
      const attemptCount = attemptCountMap[exam.ExamID] || 0;

      // Find the latest attempt for this exam
      const attempts = studentExams.filter(
        sExam => sExam.examId === exam.ExamID
      );
      const latestAttempt =
        attempts.length > 0
          ? attempts.sort(
              (a, b) => new Date(b.startTime) - new Date(a.startTime)
            )[0]
          : null;

      return {
        examId: exam.ExamID,
        title: exam.ExamName,
        description: exam.Description,
        category: exam.Category,
        displayOrder: exam.DisplayOrder,
        attemptCount: attemptCount,
        latestAttempt,
      };
    }

    // Format date in compact way
    function formatDate(isoString) {
      if (!isoString) return '—';
      const d = new Date(isoString);
      return d.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    // Format timeSpentMinutes => XXXmin
    function formatTimeSpent(minutesVal) {
      if (minutesVal == null) return '—';
      const mm = Math.floor(minutesVal);
      return `${mm}m`;
    }

    // Start or repeat exam => triggers simulator
    async function handleStartExam(examId, examTitle) {
      try {
        const res = await ApiStore.startExamAttempt(examId, organizationName);
        setSelectedStudentExamId(res.student_exam_id);
        setExamTitle(examTitle);
      } catch (err) {
        console.error('Error starting exam:', err);
      }
    }

    // Toggle expanded state for an exam
    const toggleExpand = examId => {
      setExpandedExamIds(prev =>
        prev.includes(examId)
          ? prev.filter(id => id !== examId)
          : [...prev, examId]
      );
    };

    // Group exams by category
    const examsByCategory = filteredExams.reduce((acc, exam) => {
      const category = exam.Category || 'Sin categoría';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(exam);
      return acc;
    }, {});

    // Sort categories to show "Sin categoría" last
    const sortedCategories = Object.keys(examsByCategory).sort((a, b) => {
      if (a === 'Sin categoría') return 1;
      if (b === 'Sin categoría') return -1;
      return a.localeCompare(b);
    });

    // Progress stats
    const completedExamIds = new Set(
      studentExams.filter(e => e.status === 'completed').map(e => e.examId)
    );
    const totalExamsCount = allExams.length;
    const completedCount = completedExamIds.size;
    const progressPercentage = totalExamsCount
      ? (completedCount / totalExamsCount) * 100
      : 0;

    return (
      <div className="min-h-screen p-3 sm:p-6 bg-transparent">
        <div className="flex-grow max-w-6xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">
            Exámenes
          </h1>

          {/* Progress Bar - Visible on all screens */}
          {!loading && (
            <div className="mb-4 sm:mb-6 bg-white rounded-lg p-3 sm:p-5 shadow-sm">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2 sm:mb-3">
                Progreso
              </h2>
              <div className="w-full bg-gray-200 rounded-full h-3 sm:h-4">
                <div
                  className="bg-blue-600 h-3 sm:h-4 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                {completedCount} de {totalExamsCount} (
                {Math.round(progressPercentage)}%)
              </p>
            </div>
          )}

          {/* Rules - Collapsed on mobile by default */}
          <div className="hidden sm:block mb-4 sm:mb-6 bg-white rounded-lg p-3 sm:p-4 border-l-4 border-yellow-400 shadow-sm">
            <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-2 flex items-center">
              <FaBook className="mr-2 text-yellow-400" />
              Reglas
            </h2>
            <ul className="space-y-1">
              {examRules.map((rule, idx) => (
                <li key={idx} className="flex items-start text-sm">
                  <span className="inline-block w-2 h-2 bg-yellow-400 rounded-full mt-1.5 mr-2" />
                  <span className="text-gray-600">{rule}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Category Tabs - Touch-friendly for mobile */}
          <div className="mb-4 sm:mb-6 bg-white rounded-lg shadow-sm overflow-x-auto">
            <div className="flex p-2 sm:p-3 items-center border-b">
              <FaTags className="text-blue-500 mr-2 flex-shrink-0" />
              <div className="flex overflow-x-auto pb-1 scrollbar-hide">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`px-3 py-1 sm:px-4 sm:py-2 rounded-md text-sm whitespace-nowrap mr-2 ${
                    selectedCategory === null
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Todas
                </button>

                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 py-1 sm:px-4 sm:py-2 rounded-md text-sm whitespace-nowrap mr-2 ${
                      selectedCategory === category
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Search Input - Full Width on Mobile */}
            <div className="p-2 sm:p-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar exámenes..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full p-2 pl-9 text-sm border rounded-md bg-gray-50"
                />
                <FaSearch className="absolute left-3 top-2.5 text-gray-400" />
              </div>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center items-center p-4 sm:p-8">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-t-2 border-b-2 border-blue-500"></div>
              <p className="ml-2 text-sm sm:text-base text-gray-600">
                Cargando exámenes...
              </p>
            </div>
          )}

          {!loading && (
            <div>
              {sortedCategories.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 p-4">
                  <p className="text-gray-500 text-center text-sm sm:text-base">
                    No hay exámenes disponibles.
                  </p>
                </div>
              ) : (
                sortedCategories.map(category => {
                  // All categories should be expanded by default, including "Sin categoría"
                  const categoryId = `category_${category}`;

                  // Check if this category has been manually collapsed
                  const wasManuallyCollapsed = expandedExamIds.includes(
                    `collapsed_${categoryId}`
                  );

                  // Category is expanded if it's in the expanded list OR categories are ready AND it hasn't been manually collapsed
                  const isCategoryExpanded =
                    expandedExamIds.includes(categoryId) ||
                    (categoriesReady && !wasManuallyCollapsed);

                  const toggleCategory = () => {
                    if (isCategoryExpanded) {
                      // When collapsing: remove from expanded list and add to collapsed list
                      setExpandedExamIds(prev => {
                        const newState = prev.filter(id => id !== categoryId);
                        newState.push(`collapsed_${categoryId}`); // Mark as manually collapsed
                        return newState;
                      });
                    } else {
                      // When expanding: add to expanded list and remove from collapsed list
                      setExpandedExamIds(prev => {
                        const newState = [...prev, categoryId];
                        return newState.filter(
                          id => id !== `collapsed_${categoryId}`
                        );
                      });
                    }
                  };

                  // Calculate category progress
                  const categoryExams = examsByCategory[category];
                  const totalExamsInCategory = categoryExams.length;
                  const completedExamsInCategory = categoryExams.filter(
                    exam => {
                      const status = getStatusForExam(exam);
                      return (
                        status.latestAttempt &&
                        status.latestAttempt.status === 'completed' &&
                        (status.latestAttempt.score || 0) >= 90
                      );
                    }
                  ).length;
                  const categoryProgress =
                    totalExamsInCategory > 0
                      ? Math.round(
                          (completedExamsInCategory / totalExamsInCategory) *
                            100
                        )
                      : 0;

                  return (
                    <div
                      key={category}
                      className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 last:mb-0"
                    >
                      <div
                        className="flex items-center justify-between p-3 sm:p-4 cursor-pointer border-b"
                        onClick={toggleCategory}
                      >
                        <div className="flex flex-col">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700">
                            {category}
                          </h3>
                          <div className="text-xs text-gray-500 mt-1">
                            <span
                              className={
                                completedExamsInCategory > 0
                                  ? 'text-green-600 font-medium'
                                  : 'text-gray-500'
                              }
                            >
                              {completedExamsInCategory}
                            </span>
                            <span>
                              {' '}
                              / {totalExamsInCategory} completados (
                              {categoryProgress}%)
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="w-16 h-2 bg-gray-200 rounded-full mr-2 hidden sm:block">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${categoryProgress}%` }}
                            />
                          </div>
                          {isCategoryExpanded ? (
                            <FaChevronUp />
                          ) : (
                            <FaChevronDown />
                          )}
                        </div>
                      </div>

                      {isCategoryExpanded && (
                        <div className="p-0 sm:p-4">
                          {/* Mobile Card Layout */}
                          <div className="block sm:hidden">
                            {examsByCategory[category]
                              .sort((a, b) => {
                                // Sort by DisplayOrder first (if present)
                                if (
                                  a.DisplayOrder !== null &&
                                  b.DisplayOrder !== null
                                ) {
                                  return a.DisplayOrder - b.DisplayOrder;
                                } else if (a.DisplayOrder !== null) {
                                  return -1;
                                } else if (b.DisplayOrder !== null) {
                                  return 1;
                                }
                                // Then by name
                                return a.ExamName.localeCompare(b.ExamName);
                              })
                              .map(exam => {
                                const status = getStatusForExam(exam);
                                const latestAttempt = status.latestAttempt;

                                // Status logic
                                let statusColor = 'bg-blue-500'; // Not started
                                let statusTooltip = 'No iniciado';

                                if (latestAttempt) {
                                  if (latestAttempt.status === 'completed') {
                                    if ((latestAttempt.score || 0) >= 90) {
                                      statusColor = 'bg-green-500';
                                      statusTooltip = 'Aprobado';
                                    } else {
                                      statusColor = 'bg-red-500';
                                      statusTooltip = 'Reprobado';
                                    }
                                  } else if (
                                    latestAttempt.status === 'in_progress'
                                  ) {
                                    statusColor = 'bg-yellow-500';
                                    statusTooltip = 'Incompleto';
                                  }
                                }

                                const isExpanded = expandedExamIds.includes(
                                  exam.ExamID
                                );

                                return (
                                  <div
                                    key={exam.ExamID}
                                    className="border-b last:border-b-0"
                                  >
                                    <div className="p-3 flex items-center">
                                      {/* Status Indicator */}
                                      <div
                                        className={`w-3 h-3 rounded-full ${statusColor} mr-3 flex-shrink-0`}
                                        title={statusTooltip}
                                      />

                                      {/* Exam Info */}
                                      <div className="flex-grow min-w-0">
                                        <h4 className="font-medium text-gray-900 truncate">
                                          {exam.ExamName}
                                        </h4>
                                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                          {exam.Description}
                                        </p>

                                        {/* Latest score if available */}
                                        {latestAttempt &&
                                          latestAttempt.status ===
                                            'completed' && (
                                            <div className="flex items-center mt-1 text-xs">
                                              <FaTrophy className="text-yellow-400 mr-1" />
                                              <span className="font-medium">
                                                {latestAttempt.score || 0}%
                                              </span>
                                              <span className="mx-1 text-gray-400">
                                                •
                                              </span>
                                              <span>
                                                {formatTimeSpent(
                                                  latestAttempt.timeSpentMinutes
                                                )}
                                              </span>
                                              <span className="mx-1 text-gray-400">
                                                •
                                              </span>
                                              <span>
                                                {formatDate(
                                                  latestAttempt.startTime
                                                )}
                                              </span>
                                            </div>
                                          )}
                                      </div>

                                      {/* Action Buttons */}
                                      <div className="flex items-center ml-2">
                                        {status.attemptCount > 0 && (
                                          <button
                                            onClick={() =>
                                              toggleExpand(exam.ExamID)
                                            }
                                            className="p-1 mr-2 text-blue-500"
                                            aria-label="Ver historial"
                                            title="Ver historial"
                                          >
                                            {isExpanded ? (
                                              <FaChevronUp />
                                            ) : (
                                              <FaChevronDown />
                                            )}
                                          </button>
                                        )}

                                        <button
                                          className={`p-2 rounded-full ${
                                            latestAttempt &&
                                            latestAttempt.status === 'completed'
                                              ? 'bg-blue-100 text-blue-600'
                                              : 'bg-green-100 text-green-600'
                                          }`}
                                          onClick={() =>
                                            handleStartExam(
                                              exam.ExamID,
                                              exam.ExamName
                                            )
                                          }
                                          aria-label={
                                            latestAttempt &&
                                            latestAttempt.status === 'completed'
                                              ? 'Repetir examen'
                                              : 'Iniciar examen'
                                          }
                                          title={
                                            latestAttempt &&
                                            latestAttempt.status === 'completed'
                                              ? 'Repetir examen'
                                              : 'Iniciar examen'
                                          }
                                        >
                                          {latestAttempt &&
                                          latestAttempt.status ===
                                            'completed' ? (
                                            <FaRedo size={14} />
                                          ) : (
                                            <FaClock size={14} />
                                          )}
                                        </button>
                                      </div>
                                    </div>

                                    {/* Expanded Attempts History */}
                                    {isExpanded && status.attemptCount > 0 && (
                                      <div className="px-3 pb-3 bg-gray-50">
                                        <div className="text-xs font-medium text-gray-700 mb-2">
                                          Historial ({status.attemptCount})
                                        </div>
                                        {studentExams
                                          .filter(
                                            ex =>
                                              ex.examId === exam.ExamID &&
                                              ex.status === 'completed'
                                          )
                                          .sort(
                                            (a, b) =>
                                              new Date(b.startTime) -
                                              new Date(a.startTime)
                                          )
                                          .map((attempt, idx) => {
                                            let attemptStatusColor =
                                              (attempt.score || 0) >= 90
                                                ? 'bg-green-500'
                                                : 'bg-red-500';

                                            return (
                                              <div
                                                key={idx}
                                                className="flex items-center py-1 border-t border-gray-200 text-xs"
                                              >
                                                <div
                                                  className={`w-2 h-2 rounded-full ${attemptStatusColor} mr-2`}
                                                />
                                                <div className="w-16">
                                                  {formatDate(
                                                    attempt.startTime
                                                  )}
                                                </div>
                                                <div className="w-14 flex items-center">
                                                  <FaTrophy
                                                    className="text-yellow-400 mr-1"
                                                    size={10}
                                                  />
                                                  <span>
                                                    {attempt.score ?? 0}%
                                                  </span>
                                                </div>
                                                <div className="flex-grow text-right">
                                                  {formatTimeSpent(
                                                    attempt.timeSpentMinutes
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>

                          {/* Desktop Table Layout */}
                          <div className="hidden sm:block overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Estado
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Examen
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Último Intento
                                  </th>
                                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Historial
                                  </th>
                                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acción
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {examsByCategory[category]
                                  .sort((a, b) => {
                                    // Sort by DisplayOrder first (if present)
                                    if (
                                      a.DisplayOrder !== null &&
                                      b.DisplayOrder !== null
                                    ) {
                                      return a.DisplayOrder - b.DisplayOrder;
                                    } else if (a.DisplayOrder !== null) {
                                      return -1;
                                    } else if (b.DisplayOrder !== null) {
                                      return 1;
                                    }
                                    // Then by name
                                    return a.ExamName.localeCompare(b.ExamName);
                                  })
                                  .map(exam => {
                                    const status = getStatusForExam(exam);
                                    const latestAttempt = status.latestAttempt;

                                    // Status logic
                                    let statusColor = 'bg-blue-500'; // Not started
                                    let statusTooltip = 'No iniciado';

                                    if (latestAttempt) {
                                      if (
                                        latestAttempt.status === 'completed'
                                      ) {
                                        if ((latestAttempt.score || 0) >= 90) {
                                          statusColor = 'bg-green-500';
                                          statusTooltip = 'Aprobado';
                                        } else {
                                          statusColor = 'bg-red-500';
                                          statusTooltip = 'Reprobado';
                                        }
                                      } else if (
                                        latestAttempt.status === 'in_progress'
                                      ) {
                                        statusColor = 'bg-yellow-500';
                                        statusTooltip = 'Incompleto';
                                      }
                                    }

                                    const isExpanded = expandedExamIds.includes(
                                      exam.ExamID
                                    );

                                    return (
                                      <React.Fragment key={exam.ExamID}>
                                        <tr className="hover:bg-gray-50">
                                          {/* Status Indicator */}
                                          <td className="px-3 py-3 whitespace-nowrap">
                                            <div
                                              className={`w-4 h-4 rounded-full ${statusColor}`}
                                              title={statusTooltip}
                                            />
                                          </td>

                                          {/* Exam Title & Description */}
                                          <td className="px-3 py-3">
                                            <div className="text-sm font-medium text-gray-900">
                                              {exam.ExamName}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1 line-clamp-2 max-w-sm">
                                              {exam.Description}
                                            </div>
                                          </td>

                                          {/* Latest Attempt */}
                                          <td className="px-3 py-3 text-sm text-gray-700">
                                            {latestAttempt ? (
                                              <div>
                                                <div>
                                                  {formatDate(
                                                    latestAttempt.startTime
                                                  )}
                                                </div>
                                                {latestAttempt.status ===
                                                  'completed' && (
                                                  <div className="flex items-center mt-1 text-xs">
                                                    <FaTrophy className="text-yellow-400 mr-1" />
                                                    <span className="font-medium">
                                                      {latestAttempt.score || 0}
                                                      %
                                                    </span>
                                                    <span className="mx-1 text-gray-400">
                                                      •
                                                    </span>
                                                    <span>
                                                      {formatTimeSpent(
                                                        latestAttempt.timeSpentMinutes
                                                      )}
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              <span>—</span>
                                            )}
                                          </td>

                                          {/* Attempts History Toggle */}
                                          <td className="px-3 py-3 text-center">
                                            {status.attemptCount > 0 ? (
                                              <button
                                                onClick={() =>
                                                  toggleExpand(exam.ExamID)
                                                }
                                                className="inline-flex items-center text-blue-500 hover:text-blue-700"
                                                title="Ver historial de intentos"
                                              >
                                                {status.attemptCount}
                                                {isExpanded ? (
                                                  <FaChevronUp className="ml-1" />
                                                ) : (
                                                  <FaChevronDown className="ml-1" />
                                                )}
                                              </button>
                                            ) : (
                                              <span>0</span>
                                            )}
                                          </td>

                                          {/* Action Button */}
                                          <td className="px-3 py-3 text-center">
                                            <button
                                              className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${
                                                latestAttempt &&
                                                latestAttempt.status ===
                                                  'completed'
                                                  ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                                  : 'bg-green-100 text-green-600 hover:bg-green-200'
                                              }`}
                                              onClick={() =>
                                                handleStartExam(
                                                  exam.ExamID,
                                                  exam.ExamName
                                                )
                                              }
                                            >
                                              {latestAttempt &&
                                              latestAttempt.status ===
                                                'completed' ? (
                                                <>
                                                  <FaRedo
                                                    className="inline mr-1"
                                                    size={12}
                                                  />{' '}
                                                  Repetir
                                                </>
                                              ) : (
                                                <>
                                                  <FaClock
                                                    className="inline mr-1"
                                                    size={12}
                                                  />{' '}
                                                  Iniciar
                                                </>
                                              )}
                                            </button>
                                          </td>
                                        </tr>

                                        {/* Expanded Attempts History */}
                                        {isExpanded &&
                                          status.attemptCount > 0 && (
                                            <tr>
                                              <td
                                                colSpan="5"
                                                className="px-4 py-2 bg-gray-50"
                                              >
                                                <div className="text-xs font-medium text-gray-700 mb-2">
                                                  Historial de intentos:
                                                </div>
                                                <table className="w-full text-sm">
                                                  <thead className="bg-gray-100 text-xs">
                                                    <tr>
                                                      <th className="px-2 py-1 text-left font-medium text-gray-500">
                                                        Estado
                                                      </th>
                                                      <th className="px-2 py-1 text-left font-medium text-gray-500">
                                                        Fecha
                                                      </th>
                                                      <th className="px-2 py-1 text-left font-medium text-gray-500">
                                                        Puntuación
                                                      </th>
                                                      <th className="px-2 py-1 text-left font-medium text-gray-500">
                                                        Tiempo
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody className="divide-y divide-gray-200 text-xs">
                                                    {studentExams
                                                      .filter(
                                                        ex =>
                                                          ex.examId ===
                                                            exam.ExamID &&
                                                          ex.status ===
                                                            'completed'
                                                      )
                                                      .sort(
                                                        (a, b) =>
                                                          new Date(
                                                            b.startTime
                                                          ) -
                                                          new Date(a.startTime)
                                                      )
                                                      .map((attempt, idx) => {
                                                        let attemptStatusColor =
                                                          (attempt.score ||
                                                            0) >= 90
                                                            ? 'bg-green-500'
                                                            : 'bg-red-500';
                                                        let attemptTooltip =
                                                          (attempt.score ||
                                                            0) >= 90
                                                            ? 'Aprobado'
                                                            : 'Reprobado';

                                                        return (
                                                          <tr
                                                            key={idx}
                                                            className="hover:bg-white"
                                                          >
                                                            <td className="px-2 py-1">
                                                              <div
                                                                className={`w-3 h-3 rounded-full ${attemptStatusColor}`}
                                                                title={
                                                                  attemptTooltip
                                                                }
                                                              />
                                                            </td>
                                                            <td className="px-2 py-1 text-gray-700">
                                                              {formatDate(
                                                                attempt.startTime
                                                              )}
                                                            </td>
                                                            <td className="px-2 py-1">
                                                              <div className="flex items-center">
                                                                <FaTrophy
                                                                  className="text-yellow-400 mr-1"
                                                                  size={10}
                                                                />
                                                                <span className="font-medium text-gray-900">
                                                                  {attempt.score ??
                                                                    0}
                                                                  %
                                                                </span>
                                                              </div>
                                                            </td>
                                                            <td className="px-2 py-1 text-gray-700">
                                                              {formatTimeSpent(
                                                                attempt.timeSpentMinutes
                                                              )}
                                                            </td>
                                                          </tr>
                                                        );
                                                      })}
                                                  </tbody>
                                                </table>
                                              </td>
                                            </tr>
                                          )}
                                      </React.Fragment>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Status Legend - Only on Mobile */}
          <div className="block sm:hidden bg-white rounded-lg p-3 shadow-sm text-xs">
            <h3 className="font-medium text-gray-700 mb-2 flex items-center">
              <FaInfoCircle className="mr-1 text-blue-500" /> Leyenda
            </h3>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
                <span>Aprobado</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div>
                <span>Reprobado</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-1"></div>
                <span>Incompleto</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-blue-500 mr-1"></div>
                <span>No iniciado</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  })
);

export default StudentDashboard;
